import React, { useState, useRef, useEffect, useContext } from 'react';
import { axisBottom, axisRight } from 'd3-axis';
import { scaleLinear, scaleBand } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { line } from 'd3-shape';
import { format } from 'd3-format';
import { object, number, array, string } from 'prop-types';
import { SelectionContext } from './context';

const Chart = ( props ) => {

    const { selectionQuarters } = useContext( SelectionContext );
    const { width, allQuarters, selectedData, vps, chartSubtitle, chartContainer } = props;
    const { margin, height, mobile } = props.chart;

    const allDataEmpty = selectedData.every( s => s[ 'column' ][ 'value' ].every( c => c === null || c === undefined ) );

    /* States and refs */
    const [ chartData, setChartData ] = useState( selectedData );
    const [ tooltipData, setTooltipData ] = useState( selectedData[ 0 ] );
    const [ yDomain, setYDomain ] = useState( {} );
    const [ trimmedQuarters, setTrimmedQuarters ] = useState( [] );

    const svgRef = useRef();
    const axisXRef = useRef();
    const axisYRef = useRef();
    const lineRef = useRef();
    const scrubberRef = useRef();
    const tooltipRef = useRef();

    useEffect( () => {

        if ( selectedData.length !== 0 ) {

            const quarterIndices = [];

            for ( const qtr of selectionQuarters ) {

                const found = selectedData.find( ( { column: { quarter } } ) => quarter === qtr );

                quarterIndices.push( selectedData.indexOf( found ) );

            }

            const quarterSlicedData = selectedData.slice( quarterIndices[ 0 ], quarterIndices[ 1 ] + 1 );

            const allRowValues = quarterSlicedData
                .reduce( ( acc, row ) => [ ...acc, ...row[ 'rows' ].map( ( { value } ) => value ) ], [] )
                .filter( value => value !== null && value !== undefined );

            setYDomain( {
                minValue: Math.min( ...allRowValues ),
                maxValue: Math.max( ...allRowValues ),
            } );
            setChartData( quarterSlicedData );
            setTrimmedQuarters( allQuarters.slice( quarterIndices[ 0 ], quarterIndices[ 1 ] + 1 ) );

        }

    }, [ selectedData, selectionQuarters[ 0 ], selectionQuarters[ 1 ] ] );

    useEffect( () => {

        try {

            const g = select( axisXRef.current );

            const tickDistance = parseFloat(
                g
                    .selectAll( '.tick' )
                    .filter( ( _d, i ) => i === 1 )
                    .attr( 'transform' )
                    .split( '(' )[ 1 ]
                    .split( ',' )[ 0 ],
            );

            if ( tickDistance <= 90 ) {

                g
                    .selectAll( '.tick' )
                    .filter( ( _d, i ) => i % 2 !== 0 )
                    .attr( 'display', 'none' );

                if ( tickDistance <= 50 ) {

                    g
                        .selectAll( '.tick' )
                        .filter( ( _d, i ) => i % 2 === 0 )
                        .filter( ( _d, i ) => i % 2 !== 0 )
                        .attr( 'display', 'none' );

                }
                else {

                    g
                        .selectAll( '.tick' )
                        .filter( ( _d, i ) => i % 2 === 0 )
                        .filter( ( _d, i ) => i % 2 !== 0 )
                        .attr( 'display', 'inline' );

                }

            }
            else {

                g
                    .selectAll( '.tick' )
                    .attr( 'display', 'inline' );

            }

        }
        catch ( e ) {
            // console.log('error?', e);
        }

    }, [ vps ] );

    const localMargin = width < 768 ? mobile.margin : margin;
    // Operation that scales x values of the data
    const localWidth = width - localMargin.left - localMargin.right;
    const x = scaleBand(
        trimmedQuarters,
        [ 0, localWidth ],
    );

    const localHeight = height - localMargin.top - localMargin.bottom;
    const yScaleDomain = [
        yDomain.minValue >= 0 ? yDomain.minValue * 0.9 : yDomain.minValue * 1.1,
        yDomain.maxValue >= 0 ? yDomain.maxValue * 1.1 : yDomain.maxValue * 0.9,
    ];

    if ( yScaleDomain.every( d => Math.sign( d ) === -1 ) ) {

        // all negative values
        if ( yScaleDomain[ 1 ] <= 0.05 ) yScaleDomain[ 1 ] = 0.05;

    }
    else if ( yScaleDomain.every( d => Math.sign( d ) === 1 ) ) {

        if ( yDomain.minValue - yScaleDomain[ 0 ] <= 0.08 ) yScaleDomain[ 0 ] = yDomain.minValue - 0.08;

    }
    else {

        if ( yScaleDomain[ 0 ] < 0 && yScaleDomain[ 1 ] > 0 ) {

            // negative
            if ( yScaleDomain[ 0 ] >= -0.05 ) yScaleDomain[ 0 ] = -0.05;

        }

    }

    if ( props.selectedEconVariableFilter.value !== 'Net Sentiment Change Since Last Year' ) {

        if ( yScaleDomain[ 0 ] < 0 ) yScaleDomain[ 0 ] = 0;

    }

    const y = scaleLinear()
        .domain( yScaleDomain )
        .range( [ localHeight, 0 ] )
        .nice();

    const renderEmptyChartText = () => {

        return (
            <p className="no-data-text">No data available for this combination.<br />Select another combination to generate a new chart.</p>
        );

    };

    const renderXAxis = () => {

        // Creates the x-axis path
        const xAxisPath = axisBottom( x );

        // Selects where the axis will be injected
        const g = select( axisXRef.current );

        g.call(
            xAxisPath
                .tickSizeInner( 6 )
                .tickSizeOuter( 0 )
                .tickPadding( 12 ),
        );

        g.select( '.domain' )
            .attr( 'stroke-width', '3' )
            .attr( 'stroke', '#556074' );

        g.selectAll( '.tick line' )
            .attr( 'stroke', '#515c70' )
            .attr( 'stroke-width', '1.5px' );

        g.selectAll( '.tick text' )
            .attr( 'y', 22 );

        g.select( '.tick:last-of-type text' )
            .attr( 'x', -5 );

        g.selectAll( '.tick text' )
            .attr( 'fill', '#515c70' )
            .attr( 'font-size', '15px' )
            .attr( 'font-family', 'Graphik-Medium' );

        if ( trimmedQuarters.length >= 12 ) {

            g.selectAll( '.tick text' )
                .filter( ( d, _i ) => !d.includes( 'Jan' ) )
                .attr( 'display', 'none' );

            g.selectAll( '.tick text' )
                .filter( ( d, _i ) => d.includes( 'Jan' ) )
                .text( d => d.split( ' ' )[ 1 ] );

        }
        else {

            g.selectAll( '.tick text' )
                .attr( 'display', 'inline' );

        }

        const ticks = g.selectAll( '.tick' );

        if ( ticks.size() > 0 ) {

            const tickDistance = parseFloat(
                ticks
                    .filter( ( _d, i ) => i === 1 )
                    .attr( 'transform' )
                    .split( '(' )[ 1 ]
                    .split( ',' )[ 0 ],
            );

            if ( tickDistance <= 90 ) {

                ticks
                    .filter( ( _d, i ) => i % 2 !== 0 )
                    .attr( 'display', 'none' );

                if ( tickDistance <= 50 ) {

                    ticks
                        .filter( ( _d, i ) => i % 2 === 0 )
                        .filter( ( _d, i ) => i % 2 !== 0 )
                        .attr( 'display', 'none' );

                }
                else {

                    ticks
                        .filter( ( _d, i ) => i % 2 === 0 )
                        .filter( ( _d, i ) => i % 2 !== 0 )
                        .attr( 'display', 'inline' );

                }

            }
            else {

                ticks
                    .attr( 'display', 'inline' );

            }

        }

        // Transformation to move the axis down
        const translate = `translate(0,${ localHeight })`; // this moves it down

        // Returns the html for rendering the x-axis
        return <g className="x-axis" ref={ axisXRef } transform={ translate }></g>;

    };

    const renderYAxis = () => {

        const yAxisPath = axisRight( y )
            .tickSize( width )
            .tickFormat( format( '.1%' ) )
            .ticks( 6 );

        const g = select( axisYRef.current );

        g.call( yAxisPath );

        g.select( '.domain' ).remove();

        g.selectAll( '.tick text' )
            .attr( 'x', -45 )
            .attr( 'dy', 3.5 )
            .attr( 'fill', '#515c70' )
            .attr( 'font-size', '15px' )
            .attr( 'font-family', 'Graphik-Medium' );

        g.selectAll( '.tick line' )
            .attr( 'stroke-dasharray', ( d ) => {

                return d === 0 ? '0' : '4';

            } )
            .attr( 'stroke-width', ( d ) => {

                return d === 0 ? '5' : '1';

            } )
            .attr( 'stroke', ( d ) => {

                return d === 0 ? '#929FB2' : '#929FB2';

            } )
            .attr( 'x2', width - localMargin.left - localMargin.right );

        // make sure no y axis ticks overlap with x axis
        if ( g.selectAll( '.tick' ).size() > 0 ) {

            const yTickDistance = parseFloat(
                g.selectAll( '.tick' )
                    .filter( ( _d, i ) => i === 0 )
                    .attr( 'transform' )
                    .split( '(' )[ 1 ]
                    .split( ',' )[ 1 ],
            );

            if ( Math.abs( yTickDistance - localHeight ) <= 17.0 ) {

                g.selectAll( '.tick' )
                    .filter( ( _d, i ) => i === 0 )
                    .attr( 'opacity', () => 0 );

            }

            // y axis tick text significant figures
            const yTextTrailingValues = g
                .selectAll( '.tick text' )
                .nodes()
                .map( t => parseFloat( t.textContent.split( '.' )[ 1 ].split( '%' )[ 0 ] ) );

            if ( yTextTrailingValues.every( v => v === 0 ) ) {

                // all values are 0, remove the trailing ".0" from the y axis texts
                g
                    .selectAll( '.tick text' )
                    .each( function ( d ) {

                        // eslint-disable-next-line babel/no-invalid-this
                        this.textContent = `${ this.textContent.split( '.0%' )[ 0 ] }%`;

                    } );

            }

            // right-align y axis tick text
            g
                .selectAll( '.tick text' )
                .each( function () {

                    // eslint-disable-next-line babel/no-invalid-this
                    this.setAttribute( 'x', -1 * ( this.getBBox().width + 6 ) );
                    // eslint-disable-next-line babel/no-invalid-this
                    this.textContent = this.textContent.replace( '−', '-' );

                } );

        }

        return <g className="y-axis" ref={ axisYRef }></g>;

    };

    const renderLines = () => {

        if ( svgRef.current && chartData.length > 0 ) {

            const lineGenerator = line()
                .defined( d => d[ 'value' ] !== null && d[ 'value' ] !== undefined )
                .x( d => x( d[ 'quarter' ] ) + ( x.bandwidth() / 2 ) )
                .y( d => y( d[ 'value' ] ) );

            // go throught the total number of data points to create structure for one line
            const lines = chartData[ 0 ].rows.map( ( x, i ) => {

                const color = x.color;

                const lineValues = chartData.map( ( dataPoint ) => {

                    return { quarter: dataPoint.column.quarter, value: dataPoint.rows[ i ].value };

                } );

                return {
                    color: color,
                    values: lineValues,
                };

            } );

            return lines.map( ( line, i ) => {

                return (
                    <path
                        fill="none"
                        className="line"
                        key={ i }
                        stroke={ line.color }
                        d={ lineGenerator( line.values ) }
                        strokeWidth={ 4 }
                    />
                );

            } );

        }

    };

    const renderCircles = () => {

        if ( svgRef.current && chartData.length > 0 ) {

            return chartData.map( ( { column, rows } ) => {

                // for each row that is supposed to be shown
                // get associated colorData, date and value for that date
                return rows.map( ( row, i ) => {

                    // r is half the stroke-width of the line path
                    return ( row.value !== undefined && row.value
                      && (
                          <circle
                              r="4"
                              key={ `${ column.quarter }-${ i }` }
                              className="dot-dot-dot"
                              stroke={ row.color }
                              fill={ row.color }
                              cx={ x( column.quarter ) + ( x.bandwidth() / 2 ) }
                              cy={ y( row.value ) }
                          />
                      )
                    );

                } );

            } );

        }

    };

    const renderTooltip = () => {

        const multLineTooltip = tooltipData.rows.map( ( line, i, all ) => {

            return (
                <tr key={ `line${ i }` }>
                    <td
                        className="color"
                        style={ { backgroundColor: line[ 'color' ] } }
                    >
                    </td>
                    <td className="label"> { `${ line.name }` } </td>
                    <td className="value"> { line.value !== null && line.value !== undefined ? format( '.1%' )( line.value ) : 'N/A' }</td>
                </tr>
            );

        } );

        return (
            <div className="tooltip" ref={ tooltipRef }>
                <h2>
                    { chartSubtitle }
                </h2>
                <h2>
                    { tooltipData.column.quarter }
                </h2>
                <table>
                    <tbody>{ multLineTooltip }</tbody>
                </table>
            </div>
        );

    };

    const renderHoverDots = () => {

        const chartRows = tooltipData.rows;

        const dots = chartRows.map( ( line, i ) => {

            return (
                <circle
                    r={ line.value !== null && line.value !== undefined ? '7' : '0' }
                    opacity={ 0 }
                    key={ i }
                    strokeWidth={ line.value !== null && line.value !== undefined ? 2 : 0 }
                    className="dot-per-line"
                />
            );

        } );

        return dots;

    };

    function handleSliderHover( e ) {

        if ( allDataEmpty ) {

            return;

        }

        // For hover dots
        const lines = lineRef.current.childNodes;

        // Makes tooltip and scrubber visible
        scrubberRef.current.style.opacity = 1;
        tooltipRef.current.style.display = 'block';

        // // Variables for the positioning of items on page
        const yStartVal = 0;
        const yEndVal = height - localMargin.bottom - localMargin.top;
        const xOffset = e.pageX - props.pageWrapper.offsetLeft - props.componentWrapper.offsetLeft - chartContainer.left - localMargin.left;

        // pagey, componentoffsettop, parentoffsettop, heights of titles/legend titles, tooltip height, constant
        // TODO stress test this with other components above, etc.
        // const yOffset = e.pageY - titleHeights;
        // pagey, componentoffsettop, parentoffsettop, heights of titles/legend titles, tooltip height, constant
        // also subtracting the legend, scrubber, vertical padding, basically all vertical heights that aren't the rich text top part
        // const yOffset = e.pageY - props.componentWrapper.offsetTop - props.pageWrapper.offsetTop + height - titleHeights - 94 - 64 - 100 - 153 - 48;
        const yOffset = e.pageY - ( height / 2 );

        // find the nearest X value, favoring left
        const xDiffs = x.domain().map( xr => Math.abs( x( xr ) - xOffset + ( x.bandwidth() / 2 ) ) );
        const minXDist = Math.min( ...xDiffs );
        const d = chartData[ xDiffs.indexOf( minXDist ) ];

        setTooltipData( d );

        const centerTooltip = xOffset + chartContainer.left + localMargin.left;
        const tooltipWidth = tooltipRef.current.getBoundingClientRect().width;
        const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;
        const tooltipOffset = 16;
        const centerChart = ( width - margin.right - margin.left ) / 2;
        const mobileMiddleAlignZone = 50;

        // Move tooltip
        // allows for vertical movement of box
        tooltipRef.current.style.top = `${ yOffset - ( tooltipHeight > 120 ? tooltipHeight / 2 : 20 ) }px`;

        // allows for horizontal movement
        width < 768
            ? tooltipRef.current.style.left = xOffset <= centerChart - mobileMiddleAlignZone
                ? `${ centerTooltip + tooltipOffset }px`

                : tooltipRef.current.style.left = xOffset <= centerChart + mobileMiddleAlignZone
                    ? `${ centerTooltip - ( tooltipWidth / 2 ) }px`

                    : `${ centerTooltip - tooltipWidth - tooltipOffset }px`

            : tooltipRef.current.style.left = xOffset <= centerChart
                ? `${ centerTooltip + tooltipOffset }px`

                : `${ centerTooltip - tooltipWidth - tooltipOffset }px`;

        // Move scrubber line
        select( '.scrubber' ).attr( 'd', () => {

            let d = `M${ xOffset },${ yStartVal }`;

            d += ` ${ xOffset },${ yEndVal }`;

            return d;

        } );

        // Move dots along lines
        const x2 = x( d.column.quarter ) + ( x.bandwidth() / 2 );

        selectAll( '.dot-per-line' )
            .attr( 'transform', ( data, i ) => {

                if ( d.rows[ i ].value === undefined || d.rows[ i ].value === null ) return `translate(0, 0)`;

                const y2 = y( d.rows[ i ].value );

                return `translate(${ x2 },${ y2 })`;

            } )
            .attr( 'fill', '#ffffff' )
            .attr( 'stroke', ( data, i ) => {

                if ( lines[ i ] !== undefined ) {

                    return lines[ i ].getAttribute( 'stroke' );

                }

            } )
            .attr( 'opacity', ( data, i ) => {

                return d.rows[ i ].value === undefined || d.rows[ i ].value === null ? 0 : 1;

            } );

    }

    const handleSliderOut = ( _e ) => {

        if ( allDataEmpty ) {

            return;

        }

        scrubberRef.current.style.opacity = 0;
        tooltipRef.current.style.display = 'none';

        const mouseDots = Array.from(
            document.getElementsByClassName( 'dot-per-line' ),
        );

        mouseDots.forEach( ( hoverDot ) => {

            hoverDot.setAttribute( 'opacity', 0 );

        } );

    };

    return (
        <div className={ `data-visualization-section ${ allDataEmpty ? 'relative-empty-data' : '' }` } style={ { height: `${ height }px` } }>
            { allDataEmpty ? renderEmptyChartText() : '' }
            <svg ref={ svgRef } width={ width } height={ height }>

                <rect
                    data-desc="background color for IE/Edge"
                    fill="#fff"
                    width={ width }
                    height={ height }
                />

                <g transform={ `translate(${ localMargin.left }, ${ localMargin.top })` }>
                    { allDataEmpty ? '' : renderYAxis() }
                    { renderXAxis() }
                    <g className="line-render" ref={ lineRef }>
                        { renderLines() }
                        { renderCircles() }
                    </g>
                    <g className="scrubber-section">
                        <path className="scrubber" ref={ scrubberRef }></path>
                        { renderHoverDots() }
                        <rect
                            className="mouse-over-canvas"
                            fill="none"
                            pointerEvents="all"
                            height={ localHeight }
                            width={ width - localMargin.right - localMargin.left }
                            onMouseMove={ e => handleSliderHover( e ) }
                            onMouseOut={ e => handleSliderOut( e ) }
                        />
                    </g>
                </g>
            </svg>
            { allDataEmpty ? '' : renderTooltip() }

        </div>
    );

};

Chart.propTypes = {
    allQuarters: array.isRequired,
    chartSubtitle: string.isRequired,
    pageWrapper: object.isRequired,
    componentWrapper: object.isRequired,
    selectedData: array.isRequired,
    chart: object,
    width: number.isRequired,
    titleHeights: number.isRequired,
    vps: object.isRequired,
    selectedEconVariableFilter: object.isRequired,
    chartContainer: object.isRequired,
};

export default Chart;
